import { useState, useEffect } from "react";
import { API } from "@/App";
import { useContent } from "@/context/ContentContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Save, Search, FileText, Globe, Heart, PawPrint, Eye, HandHeart, Compass, MessageSquare, Coins, Repeat, Activity, Shield } from "lucide-react";

const CATEGORY_META = {
  anasayfa: { label: "Ana Sayfa", icon: Globe, color: "text-blue-600" },
  navigasyon: { label: "Navigasyon", icon: Compass, color: "text-purple-600" },
  profil: { label: "Köpek Profili", icon: PawPrint, color: "text-orange-600" },
  canlar: { label: "Canlar Sayfası", icon: Heart, color: "text-pink-600" },
  seffaflik: { label: "Şeffaflık", icon: Eye, color: "text-green-600" },
  bagis: { label: "Bağış & Ödeme", icon: HandHeart, color: "text-amber-600" },
  fiyatlar: { label: "Fiyatlar", icon: Coins, color: "text-yellow-600" },
  fonzip: { label: "Fonzip (Düzenli Bağış)", icon: Repeat, color: "text-indigo-600" },
  sahiplendirme: { label: "Sahiplendirme", icon: Heart, color: "text-red-600" },
  koruyucu_aile: { label: "Koruyucu Aile", icon: Heart, color: "text-teal-600" },
  footer: { label: "Footer", icon: FileText, color: "text-gray-600" },
  pilot: { label: "Pilot Programı", icon: Activity, color: "text-cyan-600" },
  legal: { label: "Yasal / KVKK", icon: Shield, color: "text-slate-700" },
  genel: { label: "Genel", icon: FileText, color: "text-gray-600" },
};

export default function ContentManagement() {
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState(null);
  const [editedValues, setEditedValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { refresh } = useContent();

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const res = await axios.get(`${API}/content-categories`);
      setCategories(res.data);
      const cats = Object.keys(res.data);
      if (cats.length > 0 && !activeCategory) setActiveCategory(cats[0]);
    } catch (err) {
      toast.error("İçerik yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setEditedValues(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), value },
    }));
  };

  const handleChangeEN = (key, value_en) => {
    setEditedValues(prev => ({
      ...prev,
      [key]: { ...(prev[key] || {}), value_en },
    }));
  };

  const handleTranslateAll = async () => {
    if (!activeCategory) return;
    const items = categories[activeCategory] || [];
    const keys = items.map(i => i.key);
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/admin/cms/translate-all`,
        { keys, overwrite: false },
        { withCredentials: true },
      );
      toast.success(`${r.data.translated} anahtar GPT-5 ile çevrildi`);
      fetchContent();
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "GPT-5 çeviri başarısız");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (Object.keys(editedValues).length === 0) {
      toast.info("Değişiklik yok");
      return;
    }
    setSaving(true);
    try {
      // editedValues is { key: { value?, value_en? } } — send as-is; backend accepts either.
      await axios.put(`${API}/content`, { items: editedValues }, { withCredentials: true });
      toast.success(`${Object.keys(editedValues).length} içerik güncellendi`);
      setEditedValues({});
      fetchContent();
      refresh();
    } catch (err) {
      console.error("CMS save failed:", err);
      toast.error("Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  };

  const getFilteredItems = () => {
    if (!activeCategory || !categories[activeCategory]) return [];
    const items = categories[activeCategory];
    if (!searchQuery) return items;
    const q = searchQuery.toLowerCase();
    return items.filter(item =>
      item.label.toLowerCase().includes(q) ||
      item.value.toLowerCase().includes(q) ||
      item.key.toLowerCase().includes(q)
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner"></div>
      </div>
    );
  }

  const filteredItems = getFilteredItems();
  const hasChanges = Object.keys(editedValues).length > 0;

  // Compute EN-empty counts per category + total, for header stats and tab badges
  const enStats = {};
  let totalKeys = 0;
  let totalEnMissing = 0;
  Object.entries(categories).forEach(([catKey, items]) => {
    const missing = items.filter(i => !(i.value_en || "").trim()).length;
    enStats[catKey] = { total: items.length, missing };
    totalKeys += items.length;
    totalEnMissing += missing;
  });
  const coverage = totalKeys > 0 ? Math.round(((totalKeys - totalEnMissing) / totalKeys) * 100) : 100;

  const handleTranslateAllMissing = async () => {
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/admin/cms/translate-all`,
        { overwrite: false },  // sadece boş EN'leri doldur
        { withCredentials: true },
      );
      toast.success(`${r.data.translated} boş EN alanı GPT-5 ile dolduruldu`);
      fetchContent();
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Toplu çeviri başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="content-management">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl">İçerik Yönetimi</h1>
          <p className="text-muted-foreground">Platform metinlerini buradan düzenleyin</p>
        </div>
        <div className="flex items-center gap-3">
          {/* EN coverage badge */}
          <div className="text-right">
            <div className="text-xs text-muted-foreground">EN Çeviri Kapsamı</div>
            <div className="text-sm font-semibold">
              <span className={coverage >= 95 ? "text-green-600" : coverage >= 80 ? "text-amber-600" : "text-red-600"}>
                %{coverage}
              </span>
              <span className="text-muted-foreground font-normal ml-1">
                ({totalKeys - totalEnMissing}/{totalKeys})
              </span>
            </div>
          </div>
          {totalEnMissing > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleTranslateAllMissing}
              disabled={saving}
              data-testid="translate-all-missing-btn"
              className="text-blue-700 border-blue-200 hover:bg-blue-50"
            >
              🌐 {saving ? "Çevriliyor…" : `${totalEnMissing} boş EN alanını GPT-5 ile doldur`}
            </Button>
          )}
          {hasChanges && (
            <Button onClick={handleSave} disabled={saving} className="bg-primary text-primary-foreground" data-testid="save-content-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? "Kaydediliyor..." : `${Object.keys(editedValues).length} Değişikliği Kaydet`}
            </Button>
          )}
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(categories).map(([catKey, items]) => {
          const meta = CATEGORY_META[catKey] || { label: catKey, icon: FileText, color: "text-gray-600" };
          const Icon = meta.icon;
          const stat = enStats[catKey] || { total: items.length, missing: 0 };
          return (
            <button
              key={catKey}
              onClick={() => setActiveCategory(catKey)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeCategory === catKey
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "bg-muted/50 hover:bg-muted text-muted-foreground"
              }`}
              data-testid={`content-tab-${catKey}`}
            >
              <Icon className="w-4 h-4" />
              {meta.label}
              <span className="text-xs opacity-70">({stat.total})</span>
              {stat.missing > 0 && (
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                    activeCategory === catKey
                      ? "bg-white/25 text-white"
                      : "bg-amber-100 text-amber-800"
                  }`}
                  data-testid={`content-tab-en-missing-${catKey}`}
                  title={`${stat.missing} anahtarın EN çevirisi eksik`}
                >
                  {stat.missing} EN boş
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="İçeriklerde ara..."
          className="pl-10"
          data-testid="content-search"
        />
      </div>

      {/* Content Items */}
      <div className="space-y-3">
        {activeCategory && filteredItems.length > 0 && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={handleTranslateAll}
              disabled={saving}
              data-testid="translate-category-en-btn"
              className="text-blue-700 border-blue-200 hover:bg-blue-50"
            >
              🌐 {saving ? "Çevriliyor…" : "Boş EN alanlarını GPT-5 ile doldur"}
            </Button>
          </div>
        )}
        {filteredItems.map((item) => {
          const edit = editedValues[item.key] || {};
          const currentValue = edit.value !== undefined ? edit.value : item.value;
          const currentValueEN = edit.value_en !== undefined ? edit.value_en : (item.value_en || "");
          const isEdited = editedValues[item.key] !== undefined;

          return (
            <Card key={item.key} className={`border ${isEdited ? "border-primary/50 bg-primary/5" : "border-border/50"}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                      <label className="text-sm font-medium">{item.label}</label>
                      {isEdited && <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded-full">düzenlendi</span>}
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                          <span className="px-1.5 py-0.5 rounded bg-red-50 text-red-700">TR</span>
                          <span>Türkçe</span>
                        </div>
                        {item.type === "textarea" ? (
                          <Textarea
                            value={currentValue}
                            onChange={(e) => handleChange(item.key, e.target.value)}
                            rows={2}
                            className="text-sm"
                            data-testid={`content-edit-${item.key}`}
                          />
                        ) : (
                          <Input
                            type={item.type === "number" ? "number" : "text"}
                            value={currentValue}
                            onChange={(e) => handleChange(item.key, e.target.value)}
                            className="text-sm"
                            data-testid={`content-edit-${item.key}`}
                          />
                        )}
                      </div>
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1">
                          <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">EN</span>
                          <span>English</span>
                          {!currentValueEN && <span className="ml-1 text-amber-600">· boş</span>}
                        </div>
                        {item.type === "textarea" ? (
                          <Textarea
                            value={currentValueEN}
                            onChange={(e) => handleChangeEN(item.key, e.target.value)}
                            rows={2}
                            className="text-sm"
                            placeholder="English translation (optional)"
                            data-testid={`content-edit-en-${item.key}`}
                          />
                        ) : (
                          <Input
                            type={item.type === "number" ? "number" : "text"}
                            value={currentValueEN}
                            onChange={(e) => handleChangeEN(item.key, e.target.value)}
                            className="text-sm"
                            placeholder="English translation (optional)"
                            data-testid={`content-edit-en-${item.key}`}
                          />
                        )}
                      </div>
                    </div>
                    <p className="text-[11px] text-muted-foreground/60 font-mono">{item.key}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {filteredItems.length === 0 && (
          <p className="text-center text-muted-foreground py-8">
            {searchQuery ? "Aramayla eşleşen içerik bulunamadı" : "Bu kategoride içerik yok"}
          </p>
        )}
      </div>

      {/* Floating Save Button */}
      {hasChanges && (
        <div className="fixed bottom-6 right-6 z-50">
          <Button onClick={handleSave} disabled={saving} size="lg" className="bg-primary text-primary-foreground shadow-xl rounded-full px-6" data-testid="save-content-floating">
            <Save className="w-5 h-5 mr-2" />
            {saving ? "Kaydediliyor..." : `Kaydet (${Object.keys(editedValues).length})`}
          </Button>
        </div>
      )}
    </div>
  );
}
